/**
 * UGC Ad Templates — seeds the landing page gallery at `/` and pre-fills `/create/ugc`.
 *
 * Ship model: each template is a fully self-contained bundle (avatar + script + preview video).
 * No product URLs, no placeholders. User clicks a tile → auth → `/create/ugc?template={id}`
 * loads the avatar + script pre-filled, one click to Generate.
 *
 * Category: single `TALKING_AVATAR` for W17 launch. More categories ship in W18+ as library grows.
 *
 * Seed status (W17): seeded via `scripts/app/vidmax/seed-ugc-templates.ts` on 2026-04-26 — all 6 entries
 * have real `vm_Avatar.id` and `vm_Voice.id` cuids pointing at Unsplash stock face photos
 * + ElevenLabs public preset voices. Re-running the script is idempotent (matches by name).
 * `modelHint: EUgcGenerationModel.OMNIHUMAN` is the cheapest fal.ai lipsync ($0.04/sec).
 */
import { asAvatarId, asVoiceId } from "../types/branded"
import type { AvatarId, VoiceId } from "../types/branded"
import { EUgcGenerationModel } from "./ugcGenerationModels"

export enum EUgcTemplateCategory {
  TALKING_AVATAR = "talking_avatar",
}

export enum EUgcTemplateHookType {
  PAIN = "pain",
  OUTCOME = "outcome",
  CURIOSITY = "curiosity",
  TESTIMONIAL = "testimonial",
  UNBOXING = "unboxing",
}

export interface UgcTemplate {
  id: string
  category: EUgcTemplateCategory
  /** vm_Avatar.id (cuid) — joined against the DB at runtime; row must have non-null url + deletedAt=null. */
  avatarId: AvatarId
  /** vm_Voice.id (cuid) — NEVER an ElevenLabs externalVoiceId; resolution to externalVoiceId happens server-side. */
  voiceId: VoiceId
  /** Preferred lipsync model. Server falls back if the model is retired/unavailable (W18+). */
  modelHint: EUgcGenerationModel
  hookType: EUgcTemplateHookType
  script: string
  previewVideoUrl: string
  thumbnailUrl: string
  title: string
  /** Forward-compat: free-form tags for filtering / search. */
  tags?: string[]
  /** Forward-compat: gates which modes (talking-avatar / scene-gen / native-dialogue) the template supports. */
  supportedModes?: string[]
}

/** Cheapest fal.ai LIPSYNC path model (path=LIPSYNC, providerFamily=fal-avatar, costPerSec=0.04). */
const DEFAULT_MODEL_HINT = EUgcGenerationModel.OMNIHUMAN

// Sample mp4s live on the shared CDN. They were rendered through the real lipsync
// pipeline (vm_AssetJob → vm_Image.uploadedUrl) and pinned here so the trending row
// has stable URLs without a per-render DB lookup. `thumbnailUrl` is the first frame
// of the corresponding mp4, extracted via ffmpeg and uploaded to the same CDN
// (cdn.aixlabs.co/ugc-templates/talking_avatar_NN.jpg).
export const UGC_TEMPLATES: UgcTemplate[] = [
  {
    id: "talking_avatar_01",
    category: EUgcTemplateCategory.TALKING_AVATAR,
    avatarId: asAvatarId("cmofyzumy0000azozphjlelba"),
    voiceId: asVoiceId("cmofyzx4d0006azozn2ahdmzp"),
    modelHint: DEFAULT_MODEL_HINT,
    hookType: EUgcTemplateHookType.PAIN,
    title: "I wasted $3K on skincare before this",
    script:
      "I spent three thousand dollars on skincare last year and nothing worked. Then I tried this. Three weeks later, my skin looks completely different. I am never going back.",
    previewVideoUrl: "https://cdn.aixlabs.co/file_1777228328113.mp4",
    thumbnailUrl: "https://cdn.aixlabs.co/ugc-templates/talking_avatar_01.jpg",
    tags: ["beauty", "before-after", "pain-point"],
  },
  {
    id: "talking_avatar_02",
    category: EUgcTemplateCategory.TALKING_AVATAR,
    avatarId: asAvatarId("cmofyzv6q0001azozi9z1jk0e"),
    voiceId: asVoiceId("cmofyzxm10007azozxfwk34ej"),
    modelHint: DEFAULT_MODEL_HINT,
    hookType: EUgcTemplateHookType.OUTCOME,
    title: "Lost 15 lbs without the gym",
    script:
      "I dropped fifteen pounds in six weeks and did not step into a gym once. One small change is the reason. If you have tried everything and nothing stuck, this is what worked.",
    previewVideoUrl: "https://cdn.aixlabs.co/file_1777228268419.mp4",
    thumbnailUrl: "https://cdn.aixlabs.co/ugc-templates/talking_avatar_02.jpg",
    tags: ["fitness", "weight-loss", "outcome"],
  },
  {
    id: "talking_avatar_03",
    category: EUgcTemplateCategory.TALKING_AVATAR,
    avatarId: asAvatarId("cmofyzvje0002azozsxmthnuy"),
    voiceId: asVoiceId("cmofyzxyg0008azozrf8pbtar"),
    modelHint: DEFAULT_MODEL_HINT,
    hookType: EUgcTemplateHookType.CURIOSITY,
    title: "Why is no one talking about this",
    script:
      "Nobody is talking about this yet, but I just found the one thing that actually works. Two weeks in, the results speak for themselves. Wait until you see what changed.",
    previewVideoUrl: "https://cdn.aixlabs.co/file_1777228261918.mp4",
    thumbnailUrl: "https://cdn.aixlabs.co/ugc-templates/talking_avatar_03.jpg",
    tags: ["curiosity", "discovery", "trending"],
  },
  {
    id: "talking_avatar_04",
    category: EUgcTemplateCategory.TALKING_AVATAR,
    avatarId: asAvatarId("cmofyzvxm0003azozdugm1p4c"),
    voiceId: asVoiceId("cmofyzyaw0009azoze7gjelou"),
    modelHint: DEFAULT_MODEL_HINT,
    hookType: EUgcTemplateHookType.TESTIMONIAL,
    title: "I was skeptical — six months later",
    script:
      "Honestly, I was skeptical. I have been burned by so many products. But a friend kept telling me to try it, and I am so glad I did. Six months in, and I recommend it to everyone.",
    previewVideoUrl: "https://cdn.aixlabs.co/file_1777228256541.mp4",
    thumbnailUrl: "https://cdn.aixlabs.co/ugc-templates/talking_avatar_04.jpg",
    tags: ["testimonial", "long-term", "trust"],
  },
  {
    id: "talking_avatar_05",
    category: EUgcTemplateCategory.TALKING_AVATAR,
    avatarId: asAvatarId("cmofyzwcp0004azoz86d04ri2"),
    voiceId: asVoiceId("cmofyzyno000aazozk9y5q3ib"),
    modelHint: DEFAULT_MODEL_HINT,
    hookType: EUgcTemplateHookType.UNBOXING,
    title: "Unboxing this in real time",
    script:
      "It just arrived and I am opening it right now. Okay, the packaging alone is unreal. Wait. This is so much better than I expected. Let me show you what is inside.",
    previewVideoUrl: "https://cdn.aixlabs.co/file_1777228296201.mp4",
    thumbnailUrl: "https://cdn.aixlabs.co/ugc-templates/talking_avatar_05.jpg",
    tags: ["unboxing", "first-impression", "reveal"],
  },
  {
    id: "talking_avatar_06",
    category: EUgcTemplateCategory.TALKING_AVATAR,
    avatarId: asAvatarId("cmofyzwp90005azozjrhywjov"),
    voiceId: asVoiceId("cmofyzz0k000bazozuoczst75"),
    modelHint: DEFAULT_MODEL_HINT,
    hookType: EUgcTemplateHookType.PAIN,
    title: "Tired by 3pm every day? Watch this",
    script:
      "If you are tired of feeling exhausted by three p.m. every single day, stop what you are doing and listen. I found something that changed my energy completely. Two weeks, and I feel like myself again.",
    previewVideoUrl: "https://cdn.aixlabs.co/file_1777228330826.mp4",
    thumbnailUrl: "https://cdn.aixlabs.co/ugc-templates/talking_avatar_06.jpg",
    tags: ["energy", "wellness", "pain-point"],
  },
]

export function getUgcTemplateById(id: string): UgcTemplate | undefined {
  return UGC_TEMPLATES.find((t) => t.id === id)
}
